import React, { useState, useEffect } from 'react'
import {
  Box, Typography, TextField, MenuItem, Switch, FormControlLabel, Button
} from '@material-ui/core'
import axios from 'axios'

const AttributeForm = ({ serviceId, currentValues, url, onSave }) => {
  const [attributes, setAttributes] = useState([])
  const [groups, setGroups] = useState({})
  const [values, setValues] = useState(currentValues || {})
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (!serviceId || !url) return
    Promise.all([
      axios.get(`${url}/api/bid-estimation/attributes/service/${serviceId}`),
      axios.get(`${url}/api/bid-estimation/attributes/groups`)
    ]).then(([attrRes, groupRes]) => {
      setAttributes(attrRes.data || [])
      setGroups(groupRes.data || {})
    }).catch(console.error)
  }, [serviceId, url])

  useEffect(() => {
    setValues(currentValues || {})
    setDirty(false)
  }, [currentValues])

  const handleChange = (attrId, val) => {
    setValues(prev => ({ ...prev, [attrId]: val }))
    setDirty(true)
  }

  const handleSave = () => {
    if (onSave) onSave(values)
    setDirty(false)
  }

  // Group attributes by recommendedUiGroups
  const attrIds = new Set(attributes.map(a => a.id))
  const groupedAttrs = []
  const placed = new Set()

  for (const [groupName, groupAttrIds] of Object.entries(groups)) {
    const matching = groupAttrIds.filter(id => attrIds.has(id))
    if (matching.length > 0) {
      groupedAttrs.push({
        group: groupName,
        attrs: matching.map(id => attributes.find(a => a.id === id)).filter(Boolean)
      })
      matching.forEach(id => placed.add(id))
    }
  }

  // Ungrouped attributes
  const ungrouped = attributes.filter(a => !placed.has(a.id))
  if (ungrouped.length > 0) {
    groupedAttrs.push({ group: 'Other', attrs: ungrouped })
  }

  if (attributes.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary" style={{ padding: 8 }}>
        No attributes defined for this service.
      </Typography>
    )
  }

  const renderInput = (attr) => {
    const val = values[attr.id]

    switch (attr.type) {
      case 'enum':
        return (
          <TextField
            key={attr.id}
            select
            fullWidth
            size="small"
            variant="outlined"
            label={attr.name}
            value={val || ''}
            onChange={(e) => handleChange(attr.id, e.target.value)}
            helperText={attr.description}
            style={{ marginBottom: 12 }}
          >
            <MenuItem value="">— Not set —</MenuItem>
            {(attr.values || []).map(v => (
              <MenuItem key={v} value={v}>
                {v.replace(/_/g, ' ')}
              </MenuItem>
            ))}
          </TextField>
        )

      case 'boolean':
        return (
          <FormControlLabel
            key={attr.id}
            control={
              <Switch
                checked={val === true}
                onChange={(e) => handleChange(attr.id, e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2">{attr.name}</Typography>
                <Typography variant="caption" color="textSecondary">{attr.description}</Typography>
              </Box>
            }
            style={{ marginBottom: 8, display: 'flex' }}
          />
        )

      case 'number':
      case 'integer':
        return (
          <TextField
            key={attr.id}
            fullWidth
            size="small"
            variant="outlined"
            type="number"
            label={attr.name}
            value={val != null ? val : ''}
            onChange={(e) => handleChange(attr.id, e.target.value === '' ? null : Number(e.target.value))}
            helperText={`${attr.description}${attr.unit ? ` (${attr.unit})` : ''}`}
            inputProps={attr.type === 'integer' ? { step: 1 } : {}}
            style={{ marginBottom: 12 }}
          />
        )

      default: // string
        return (
          <TextField
            key={attr.id}
            fullWidth
            size="small"
            variant="outlined"
            label={attr.name}
            value={val || ''}
            onChange={(e) => handleChange(attr.id, e.target.value)}
            helperText={attr.description}
            style={{ marginBottom: 12 }}
          />
        )
    }
  }

  return (
    <Box>
      {groupedAttrs.map(({ group, attrs }) => (
        <Box key={group} mb={2}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, color: '#555' }}>
            {group}
          </Typography>
          {attrs.map(renderInput)}
        </Box>
      ))}
      {dirty && (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleSave}
          style={{ marginTop: 8 }}
        >
          Save Attributes
        </Button>
      )}
    </Box>
  )
}

export default AttributeForm
